// Wires the landing page "Install the app" button to the PWA install flow.
// Without this the button is a dead anchor pointing at its own section. Here we
// capture the browser's beforeinstallprompt event and fire it on click, with a
// manual fallback for browsers that do not support programmatic install (most
// notably iOS Safari).

interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt(): Promise<void>;
}

function isBeforeInstallPromptEvent(event: Event): event is BeforeInstallPromptEvent {
  return 'prompt' in event && 'userChoice' in event;
}

function isRunningStandalone(): boolean {
  const nav: Navigator & { standalone?: boolean } = navigator;
  return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

const installButton = document.getElementById('install-app-btn');
const installHint = document.getElementById('install-hint');

if (installButton) {
  let deferredPrompt: BeforeInstallPromptEvent | null = null;

  // Already installed and launched from the home screen: nothing to install.
  if (isRunningStandalone()) {
    installButton.hidden = true;
  }

  window.addEventListener('beforeinstallprompt', (event) => {
    if (!isBeforeInstallPromptEvent(event)) return;
    // Stop Chrome's mini-infobar so we can trigger the prompt from our button.
    event.preventDefault();
    deferredPrompt = event;
  });

  installButton.addEventListener('click', async (event) => {
    event.preventDefault();

    if (deferredPrompt) {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      // A prompt can only be used once.
      deferredPrompt = null;
      return;
    }

    // No programmatic prompt available (iOS Safari, or the browser has not
    // offered installation): show manual add-to-home-screen instructions.
    if (installHint) {
      installHint.textContent = isIos()
        ? 'On iPhone or iPad, tap the Share button, then "Add to Home Screen".'
        : 'To install, open your browser menu and choose "Install app" or "Add to Home screen".';
      installHint.hidden = false;
    }
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installButton.hidden = true;
    if (installHint) installHint.hidden = true;
  });
}
